import {
  Button,
  Checkbox,
  ColumnHeading,
  Field,
  Icon,
  pxToRem,
  Spinner,
  Table,
  Text,
  useConfirmationDialog,
} from '@lbcamden/lib-react';
import { FunctionComponent, useState } from 'react';

import { useGetPersonFullName, useGlobalStore } from '~/client/hooks/storeHooks';
import { PersonSearchResult } from '~/common/types/people';

import { NewRecordButton, PersonSearchResultsRoot } from './PersonSearchResults.styled';

type Props = {
  results: PersonSearchResult[];
  loading?: boolean;
  onClickBack(): void;
  onContinue(person?: PersonSearchResult, shouldEndPrevHouseholdMember?: boolean): void;
};

const resultsTableHeadings: ColumnHeading[] = [
  { id: 'name', content: 'Name' },
  { id: 'address', content: 'Last known address' },
  { id: 'loading', content: '' },
];

export const PersonSearchResults: FunctionComponent<Props> = ({
  results,
  loading,
  onClickBack,
  onContinue,
}) => {
  const globalStore = useGlobalStore();
  const getPersonFullName = useGetPersonFullName();

  const [selectedResult, setSelectedResult] = useState<PersonSearchResult>();
  const [shouldEndPrevHouseholdMember, setShouldEndPrevHouseholdMember] = useState(true);

  const getPropertyAddress = (propertyId?: number) =>
    propertyId ? globalStore?.get('properties', propertyId)?.address : 'Unknown';

  const { show: showConfirmEndPrevDialog, confirmationDialog: confirmEndPrevDialog } =
    useConfirmationDialog({
      title: selectedResult ? getPersonFullName(selectedResult) : '',
      message:
        `If the selected person will no longer be resident at ${getPropertyAddress(selectedResult?.propertyId)}, ` +
        'their household member status for that address should be updated.',
      children: (
        <>
          <Field label='Would you like to update their status to "ended"?' labelFor="shouldEnd">
            <Checkbox
              id="shouldEnd"
              checked={shouldEndPrevHouseholdMember}
              onChange={(e) => setShouldEndPrevHouseholdMember(e.target.checked)}
            />
          </Field>
          {shouldEndPrevHouseholdMember && (
            <Text variant="body2" $color="textSecondary">
              The previous household member status will end upon completion of the next step
            </Text>
          )}
        </>
      ),
      confirmLabel: 'Continue',
      onConfirm: () => onContinue(selectedResult, shouldEndPrevHouseholdMember),
    });

  const selectResult = (result: PersonSearchResult) => {
    const canEndHouseholdMember =
      !result.isHistoric && !result.isTenant && result.householdId && result.householdPersonId;

    setSelectedResult(result);

    if (canEndHouseholdMember) {
      showConfirmEndPrevDialog();
    } else {
      onContinue(result);
    }
  };

  return (
    <PersonSearchResultsRoot>
      <Text variant="h2">Existing people found</Text>
      <Text>
        {"The following people were found in NEC. If the person you're trying to add " +
          'is present, you should use their existing record by selecting them in the table.'}
      </Text>
      <Table
        headings={resultsTableHeadings}
        rows={results.map((result) => ({
          cells: [
            getPersonFullName(result),
            getPropertyAddress(result.propertyId),
            // oxlint-disable-next-line react/jsx-key -- prop tuple, not rendered children
            loading && selectedResult === result && <Spinner $size="1.5rem" />,
          ],
          onClickRow: loading ? undefined : () => selectResult(result),
        }))}
      />
      <Text>
        If the person you&apos;re looking for doesn&apos;t appear in the list, you can{' '}
        <NewRecordButton $variant="link" disabled={loading} onClick={() => onContinue()}>
          continue to create a new record for them.
        </NewRecordButton>
      </Text>
      <Button
        css={{ alignSelf: 'flex-start' }}
        $variant="secondary"
        disabled={loading}
        onClick={onClickBack}
      >
        <Icon icon="ui/arrow-light" width={pxToRem(14)} />
        Back
      </Button>

      {confirmEndPrevDialog}
    </PersonSearchResultsRoot>
  );
};
